import { Document, Page, Text, View, StyleSheet } from "@react-pdf/renderer";
import moment from "moment";
import "moment/locale/fr";

const styles = StyleSheet.create({
    page: {
        padding: 30,
        fontFamily: "Helvetica",
    },
    routeHeader: {
        textAlign: "center",
        marginBottom: 15,
    },
    routeTitle: {
        fontSize: 28,
        fontWeight: 700,
    },
    routeDate: {
        fontSize: 14,
        marginTop: 4,
    },
    table: {
        width: "100%",
        marginTop: 10,
    },
    tableHeader: {
        flexDirection: "row",
        backgroundColor: "#000",
        color: "#fff",
        borderBottomWidth: 1,
        borderColor: "#000",
    },
    tableRow: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderColor: "#ccc",
        minHeight: 28,
        alignItems: "center",
    },
    tableRowAlt: {
        flexDirection: "row",
        borderBottomWidth: 0.5,
        borderColor: "#ccc",
        minHeight: 28,
        alignItems: "center",
        backgroundColor: "#f5f5f5",
    },
    // Column widths
    colSequence: {
        width: "8%",
        padding: 4,
        textAlign: "center",
    },
    colPointCode: {
        width: "14%",
        padding: 4,
        textAlign: "center",
    },
    colCommerce: {
        width: "28%",
        padding: 4,
    },
    colBagCount: {
        width: "8%",
        padding: 4,
        textAlign: "center",
    },
    colBagCodes: {
        width: "42%",
        padding: 4,
    },
    headerText: {
        fontSize: 9,
        fontWeight: 700,
        color: "#fff",
        textAlign: "center",
    },
    cellText: {
        fontSize: 8,
    },
    cellTextBold: {
        fontSize: 8,
        fontWeight: 700,
    },
    summary: {
        marginTop: 15,
        flexDirection: "row",
        justifyContent: "space-between",
    },
    summaryText: {
        fontSize: 9,
    },
});

function groupByRoute(containers) {
    const routeMap = {};
    const routeOrder = [];

    for (const container of containers) {
        const route = container.routes?.[0];
        if (!route) continue;

        const routeKey = route.route_name;
        if (!routeMap[routeKey]) {
            routeMap[routeKey] = {
                routeName: route.route_name,
                hub: container.destination_hub_code || "",
                scheduledFor: route.scheduled_for,
                points: {},
                pointOrder: [],
            };
            routeOrder.push(routeKey);
        }

        const pointCode = container.point_to_code;
        if (!routeMap[routeKey].points[pointCode]) {
            routeMap[routeKey].points[pointCode] = {
                pointCode,
                name: container.name || "",
                sequence: route.sequence_in_route,
                bags: [],
            };
            routeMap[routeKey].pointOrder.push(pointCode);
        }

        routeMap[routeKey].points[pointCode].bags.push({
            label: container.container_label_code,
            cage_code: container.cage_code || null,
        });
    }

    // Sort points within each route by sequence
    for (const routeKey of routeOrder) {
        const routeData = routeMap[routeKey];
        routeData.pointOrder.sort(
            (a, b) => routeData.points[a].sequence - routeData.points[b].sequence
        );
    }

    // Sort routes by name
    routeOrder.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));

    return { routeMap, routeOrder };
}

function formatBags(bags, type) {
    if (type === "cage") {
        const cageMap = {};
        for (const bag of bags) {
            const cage = bag.cage_code || "Sans cage";
            if (!cageMap[cage]) cageMap[cage] = [];
            cageMap[cage].push(bag.label);
        }
        return Object.entries(cageMap).map(([cage, labels]) => (
            `${cage} (${labels.join(", ")})`
        ));
    }
    return [bags.map((b) => b.label).join(", ")];
}

function RouteTable({ routeData, type }) {
    const sortedPoints = routeData.pointOrder.map(
        (code) => routeData.points[code]
    );
    const totalBags = sortedPoints.reduce((sum, p) => sum + p.bags.length, 0);
    const scheduledDate = routeData.scheduledFor
        ? moment(routeData.scheduledFor).locale("fr").format("dddd DD MMMM YYYY")
        : "";

    return (
        <Page size="A4" style={styles.page}>
            <View style={styles.routeHeader}>
                <Text style={styles.routeTitle}>
                    {routeData.routeName} - {routeData.hub}
                </Text>
                <Text style={styles.routeDate}>{scheduledDate}</Text>
            </View>

            <View style={styles.table}>
                {/* Header */}
                <View style={styles.tableHeader}>
                    <View style={styles.colSequence}>
                        <Text style={styles.headerText}>#</Text>
                    </View>
                    <View style={styles.colPointCode}>
                        <Text style={styles.headerText}>Point</Text>
                    </View>
                    <View style={styles.colCommerce}>
                        <Text style={styles.headerText}>Commerce</Text>
                    </View>
                    <View style={styles.colBagCount}>
                        <Text style={styles.headerText}>Nb sac</Text>
                    </View>
                    <View style={styles.colBagCodes}>
                        <Text style={styles.headerText}>Sac</Text>
                    </View>
                </View>

                {/* Rows */}
                {sortedPoints.map((point, idx) => (
                    <View
                        key={point.pointCode}
                        style={idx % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
                        wrap={false}
                    >
                        <View style={styles.colSequence}>
                            <Text style={styles.cellTextBold}>{point.sequence}</Text>
                        </View>
                        <View style={styles.colPointCode}>
                            <Text style={styles.cellText}>{point.pointCode}</Text>
                        </View>
                        <View style={styles.colCommerce}>
                            <Text style={styles.cellText}>{point.name}</Text>
                        </View>
                        <View style={styles.colBagCount}>
                            <Text style={styles.cellTextBold}>{point.bags.length}</Text>
                        </View>
                        <View style={styles.colBagCodes}>
                            {formatBags(point.bags, type).map((line, i) => (
                                <Text key={i} style={styles.cellText}>
                                    {line}
                                </Text>
                            ))}
                        </View>
                    </View>
                ))}
            </View>

            <View style={styles.summary}>
                <Text style={styles.summaryText}>
                    {totalBags} sac{totalBags > 1 ? "s" : ""} pour {sortedPoints.length} arrêt{sortedPoints.length > 1 ? "s" : ""}
                </Text>
            </View>
        </Page>
    );
}

export default function RouteReportDocument({ containers, type }) {
    const { routeMap, routeOrder } = groupByRoute(containers);

    return (
        <Document>
            {routeOrder.map((routeKey) => (
                <RouteTable key={routeKey} routeData={routeMap[routeKey]} type={type} />
            ))}
        </Document>
    );
}
